<template>
  <div class="portfolio">
    <div class="background"></div>
    
    <transition name="fade">
      <div v-if="!selectedFile" class="upload-screen">
        <div class="upload-container">
            <h1>Pixelizer</h1>
          <p class="description">Intuitively pixelize your photos online.</p>
          
          <div class="upload-area" @dragover.prevent @drop.prevent="handleDrop">
            <input 
              type="file" 
              ref="fileInput" 
              @change="handleFileSelect" 
              accept="image/*"
              id="fileInput"
              style="display: none"
            >
            <label for="fileInput" class="upload-label">
              <p class="upload-icon">📁</p>
              <p>Click to select or drag & drop an image</p>
            </label>
          </div>
        </div>
        <div class="bottom-text-disclaimer">
          <p class="disclaimer-text">All processing is done locally in your browser. <b>Your files stay your files</b></p>
        </div>
      </div>
    </transition>

    <transition name="fade">
      <div v-if="selectedFile" class="preview-screen">
        <div class="fullscreen-preview">
          <div class="preview-with-grid" ref="previewWithGrid">
            <img :src="showPixelated && previewUrl ? previewUrl : originalPreview" alt="Image preview" class="preview-image" ref="pixelatedImage">
            <canvas 
              ref="gridCanvas" 
              class="grid-overlay"
              @mousedown="startDrag"
              @touchstart="startDrag"
            ></canvas>
          </div>
        </div>

        <transition name="slide-up">
          <div v-if="!processing" class="controls-island">
            <div class="controls-header">
              <span class="file-name">{{ selectedFile.name }}</span>
              <button @click="reset" class="close-btn">✕</button>
            </div>

            <div class="controls-content">
              <div class="control-row">
                <label>Width:</label>
                <input 
                  type="range" 
                  v-model.number="pixelWidth" 
                  min="0.5" 
                  max="200"
                  step="0.01"
                  class="slider"
                >
                <input 
                  type="number" 
                  v-model.number="pixelWidth" 
                  min="0.5" 
                  max="200"
                  step="0.01"
                  @keyup.enter="processImage"
                  ref="widthInput"
                  class="number-input"
                >
              </div>
              
              <div class="control-row">
                <label>Height:</label>
                <input 
                  type="range" 
                  v-model.number="pixelHeight" 
                  min="0.5" 
                  max="200"
                  step="0.01"
                  class="slider"
                >
                <input 
                  type="number" 
                  v-model.number="pixelHeight" 
                  min="0.5" 
                  max="200"
                  step="0.01"
                  @keyup.enter="processImage"
                  ref="heightInput"
                  class="number-input"
                >
              </div>

              <div class="control-actions">
                <label class="lock-label">
                  <input 
                    type="checkbox" 
                    v-model="lockAspectRatio"
                    class="checkbox"
                  >
                  <span>Lock</span>
                </label>
                <button @click="togglePixelated" class="reset-offset-btn" :title="showPixelated ? 'Show Original' : 'Show Pixelated'">
                  {{ showPixelated ? '◉' : '○' }}
                </button>
                <div class="button-group">
                  <button @click="processImage" class="apply-btn">Apply</button>
                  <button @click="downloadImage" class="download-btn" :disabled="!previewUrl">Download</button>
                </div>
              </div>
            </div>
          </div>
        </transition>

        <transition name="fade">
          <div v-if="processing" class="status-overlay">
            <div class="status">Processing...</div>
          </div>
        </transition>

        <transition name="fade">
          <div v-if="error" class="error-overlay">
            <div class="error">{{ error }}</div>
          </div>
        </transition>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'HelloWorld',
  data() {
    return {
      selectedFile: null,
      originalPreview: null,
      previewUrl: null,
      processing: false,
      error: null,
      convertedBlob: null,
      pixelWidth: 10,
      pixelHeight: 10,
      lockAspectRatio: true,
      originalImage: null,
      lastFocusedElement: null,
      offsetX: 0,
      offsetY: 0,
      isDragging: false,
      dragStartX: 0,
      dragStartY: 0,
      startOffsetX: 0,
      startOffsetY: 0,
      zoomLevel: 1,
      minZoom: 0.1,
      maxZoom: 10,
      showPixelated: true,
      outputWidth: null,
      outputHeight: null,
      lockOutputRatio: true,
      useCustomResolution: false
    }
  },
  watch: {
    pixelWidth(newVal, oldVal) {
      if (this.lockAspectRatio && oldVal !== 0 && oldVal !== newVal) {
        this.pixelHeight = newVal;
      }
      // Update grid in real-time
      if (this.previewUrl) {
        this.$nextTick(() => {
          this.drawGrid();
        });
      }
    },
    pixelHeight(newVal, oldVal) {
      if (this.lockAspectRatio && oldVal !== 0 && oldVal !== newVal) {
        this.pixelWidth = newVal;
      }
      // Update grid in real-time
      if (this.previewUrl && !this.lockAspectRatio) {
        this.$nextTick(() => {
          this.drawGrid();
        });
      }
    }
  },
  mounted() {
    // Add global event listeners for dragging
    window.addEventListener('mousemove', this.handleDrag);
    window.addEventListener('mouseup', this.stopDrag);
    window.addEventListener('touchmove', this.handleDrag);
    window.addEventListener('touchend', this.stopDrag);
    // Add wheel event listener for zoom
    window.addEventListener('wheel', this.handleWheel, { passive: false });
  },
  beforeUnmount() {
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
    }
    if (this.originalPreview) {
      URL.revokeObjectURL(this.originalPreview);
    }
    // Remove global event listeners
    window.removeEventListener('mousemove', this.handleDrag);
    window.removeEventListener('mouseup', this.stopDrag);
    window.removeEventListener('touchmove', this.handleDrag);
    window.removeEventListener('touchend', this.stopDrag);
    window.removeEventListener('wheel', this.handleWheel);
  },
  methods: {
    handleFileSelect(event) {
      const file = event.target.files[0];
      if (file) {
        this.loadFile(file);
      }
    },
    handleDrop(event) {
      const file = event.dataTransfer.files[0];
      if (file && file.type.startsWith('image/')) {
        this.loadFile(file);
      } else {
        this.error = 'Please drop an image file';
      }
    },
    loadFile(file) {
      this.error = null;
      this.selectedFile = file;
      this.previewUrl = null;

      const reader = new FileReader();
      reader.onload = (e) => {
        this.originalPreview = e.target.result;
        const img = new Image();
        img.onload = () => {
          this.originalImage = img;
          this.processImage();
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    },
    handleNumberInput(event) {
      this.lastFocusedElement = event.target;
      this.processImage();
    },
    startDrag(event) {
      event.preventDefault();
      this.isDragging = true;
      
      const clientX = event.touches ? event.touches[0].clientX : event.clientX;
      const clientY = event.touches ? event.touches[0].clientY : event.clientY;
      
      this.dragStartX = clientX;
      this.dragStartY = clientY;
      this.startOffsetX = this.offsetX;
      this.startOffsetY = this.offsetY;
      
      if (this.$refs.gridCanvas) {
        this.$refs.gridCanvas.style.cursor = 'grabbing';
      }
    },

    handleDrag(event) {
      if (!this.isDragging) return;
      
      event.preventDefault();
      
      const clientX = event.touches ? event.touches[0].clientX : event.clientX;
      const clientY = event.touches ? event.touches[0].clientY : event.clientY;
      
      const deltaX = clientX - this.dragStartX;
      const deltaY = clientY - this.dragStartY;
      
      // Calculate scale factor
      const img = this.$refs.pixelatedImage;
      if (!img || !this.originalImage) return;
      
      const scaleX = this.originalImage.width / img.offsetWidth;
      const scaleY = this.originalImage.height / img.offsetHeight;
      
      // Update offsets (in original image coordinates)
      this.offsetX = this.startOffsetX + (deltaX * scaleX);
      this.offsetY = this.startOffsetY + (deltaY * scaleY);
      
      // Clamp offsets to valid range
      this.offsetX = ((this.offsetX % this.pixelWidth) + this.pixelWidth) % this.pixelWidth;
      this.offsetY = ((this.offsetY % this.pixelHeight) + this.pixelHeight) % this.pixelHeight;
      
      // Redraw grid
      this.drawGrid();
    },

    stopDrag() {
      if (this.isDragging) {
        this.isDragging = false;
        if (this.$refs.gridCanvas) {
          this.$refs.gridCanvas.style.cursor = 'grab';
        }
      }
    },

    handleWheel(event) {
      // Only handle wheel events when hovering over the canvas
      if (!this.$refs.gridCanvas || !this.previewUrl) return;
      
      const canvas = this.$refs.gridCanvas;
      const rect = canvas.getBoundingClientRect();
      const mouseX = event.clientX;
      const mouseY = event.clientY;
      
      // Check if mouse is over the canvas
      if (mouseX >= rect.left && mouseX <= rect.right && 
          mouseY >= rect.top && mouseY <= rect.bottom) {
        event.preventDefault();
        
        // Adjust zoom level
        const zoomSensitivity = 0.001; // Fine control
        const delta = -event.deltaY; // Invert so scroll up zooms in
        this.zoomLevel = Math.min(this.maxZoom, Math.max(this.minZoom, this.zoomLevel + delta * zoomSensitivity));
        
        // Apply zoom to image
        this.applyZoom();
      }
    },

    applyZoom() {
      if (!this.$refs.previewWithGrid) return;
      
      const container = this.$refs.previewWithGrid;
      container.style.transform = `scale(${this.zoomLevel})`;
      container.style.transformOrigin = 'center center';
    },

    resetOffset() {
      this.offsetX = 0;
      this.offsetY = 0;
      this.zoomLevel = 1;
      if (this.$refs.previewWithGrid) {
        this.$refs.previewWithGrid.style.transform = 'scale(1)';
      }
      this.drawGrid();
    },

    processImage() {
      if (!this.originalImage) return;

      if (!this.pixelWidth || !this.pixelHeight || 
          this.pixelWidth < 0.5 || this.pixelHeight < 0.5) {
        this.error = 'Please enter valid pixel dimensions (minimum 0.5)';
        return;
      }

      this.processing = true;
      this.error = null;

      setTimeout(() => {
        try {
          const canvas = document.createElement('canvas');
          const img = this.originalImage;
          
          canvas.width = img.width;
          canvas.height = img.height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);

          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const pixels = imageData.data;

          // Use floating point dimensions with offset
          for (let y = -this.offsetY; y < canvas.height; y += this.pixelHeight) {
            for (let x = -this.offsetX; x < canvas.width; x += this.pixelWidth) {
              let r = 0, g = 0, b = 0, a = 0, count = 0;

              // Calculate the exact bounds for this cell
              const startY = Math.max(0, Math.floor(y));
              const endY = Math.min(Math.floor(y + this.pixelHeight), canvas.height);
              const startX = Math.max(0, Math.floor(x));
              const endX = Math.min(Math.floor(x + this.pixelWidth), canvas.width);

              for (let py = startY; py < endY; py++) {
                for (let px = startX; px < endX; px++) {
                  const index = (py * canvas.width + px) * 4;
                  r += pixels[index];
                  g += pixels[index + 1];
                  b += pixels[index + 2];
                  a += pixels[index + 3];
                  count++;
                }
              }

              if (count > 0) {
                r = Math.round(r / count);
                g = Math.round(g / count);
                b = Math.round(b / count);
                a = Math.round(a / count);

                ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${a / 255})`;
                ctx.fillRect(startX, startY, endX - startX, endY - startY);
              }
            }
          }

          canvas.toBlob((blob) => {
            this.convertedBlob = blob;
            if (this.previewUrl) {
              URL.revokeObjectURL(this.previewUrl);
            }
            this.previewUrl = URL.createObjectURL(blob);
            this.processing = false;
            
            // Draw grid after image is loaded
            this.$nextTick(() => {
              this.drawGrid();
              // Restore focus to the last focused element
              if (this.lastFocusedElement) {
                this.$nextTick(() => {
                  this.lastFocusedElement.focus();
                  this.lastFocusedElement = null;
                });
              }
            });
          }, 'image/png');

        } catch (err) {
          this.error = 'Processing failed: ' + err.message;
          this.processing = false;
        }
      }, 0);
    },
    drawGrid() {
      if (!this.$refs.pixelatedImage || !this.$refs.gridCanvas) return;

      const img = this.$refs.pixelatedImage;
      const canvas = this.$refs.gridCanvas;
      
      // Wait for image to load
      if (!img.complete) {
        img.onload = () => this.drawGrid();
        return;
      }

      // Scale image to fit viewport first
      this.$nextTick(() => {
        this.scaleImageToViewport();
        
        // Then draw grid after scaling
        this.$nextTick(() => {
          const displayWidth = img.offsetWidth;
          const displayHeight = img.offsetHeight;

          canvas.width = displayWidth;
          canvas.height = displayHeight;

          const ctx = canvas.getContext('2d');
          ctx.clearRect(0, 0, canvas.width, canvas.height);

          // Calculate scale factors based on original image size
          const scaleX = displayWidth / this.originalImage.width;
          const scaleY = displayHeight / this.originalImage.height;

          ctx.strokeStyle = 'rgba(66, 185, 131, 0.4)';
          ctx.lineWidth = 1;

          // Draw vertical lines with offset
          for (let x = -this.offsetX; x < this.originalImage.width; x += this.pixelWidth) {
            if (x >= 0) {
              const scaledX = x * scaleX;
              ctx.beginPath();
              ctx.moveTo(scaledX, 0);
              ctx.lineTo(scaledX, displayHeight);
              ctx.stroke();
            }
          }

          // Draw horizontal lines with offset
          for (let y = -this.offsetY; y < this.originalImage.height; y += this.pixelHeight) {
            if (y >= 0) {
              const scaledY = y * scaleY;
              ctx.beginPath();
              ctx.moveTo(0, scaledY);
              ctx.lineTo(displayWidth, scaledY);
              ctx.stroke();
            }
          }
          
          // Set cursor for dragging
          canvas.style.cursor = 'grab';
        });
      });
    },

    scaleImageToViewport() {
      if (!this.$refs.pixelatedImage || !this.originalImage) return;

      const img = this.$refs.pixelatedImage;
      
      // Get available viewport space (accounting for controls island)
      const availableWidth = window.innerWidth - 64; // 2rem padding on each side
      const availableHeight = window.innerHeight - 280; // space for controls island + padding

      // Calculate image aspect ratio
      const imageAspect = this.originalImage.width / this.originalImage.height;
      const viewportAspect = availableWidth / availableHeight;

      let targetWidth, targetHeight;

      // Scale based on which dimension is more constrained
      if (imageAspect > viewportAspect) {
        // Image is wider - fit to width
        targetWidth = availableWidth;
        targetHeight = availableWidth / imageAspect;
      } else {
        // Image is taller - fit to height
        targetHeight = availableHeight;
        targetWidth = availableHeight * imageAspect;
      }

      // Apply dimensions
      img.style.width = `${targetWidth}px`;
      img.style.height = `${targetHeight}px`;
    },
    
    downloadImage() {
      if (!this.convertedBlob) return;

      const url = URL.createObjectURL(this.convertedBlob);
      const a = document.createElement('a');
      a.href = url;
      const fileName = this.selectedFile.name.replace(/\.[^/.]+$/, '') + '_pixelated.png';
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    reset() {
      this.selectedFile = null;
      this.originalImage = null;
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl);
      }
      if (this.originalPreview) {
        URL.revokeObjectURL(this.originalPreview);
      }
      this.previewUrl = null;
      this.originalPreview = null;
      this.convertedBlob = null;
      this.error = null;
      this.pixelWidth = 10;
      this.pixelHeight = 10;
      this.lockAspectRatio = true;
      this.lastFocusedElement = null;
      this.offsetX = 0;
      this.offsetY = 0;
      this.isDragging = false;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },
    formatFileSize(bytes) {
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
    },
    togglePixelated() {
      this.showPixelated = !this.showPixelated;
      // The img src already uses: previewUrl || originalPreview
      // So when showPixelated is false, previewUrl should be null to show original
      // When showPixelated is true and previewUrl exists, it shows pixelated
    }
  }
}
</script>

<style scoped>
.portfolio {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  color: white;
  font-family: system-ui, sans-serif;
  overflow: hidden;
  user-select: none;
}

.background {
  position: fixed;
  inset: 0;
  background: #0a0a0f;
  z-index: -2;
}

/* Upload Screen */
.upload-screen {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.upload-container {
  max-width: 600px;
  width: 100%;
}

h1 {
  font-size: 2.5rem;
  font-weight: 600;
  background: linear-gradient(135deg, #ffffff 0%, #a0a0a0 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 1rem;
  text-align: center;
}

.description {
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 2rem;
  text-align: center;
  font-size: 1.1rem;
}

.upload-area {
  background: rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 3rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.upload-area:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.2);
  transform: translateY(-2px);
}

.upload-label {
  cursor: pointer;
  display: block;
  color: rgba(255, 255, 255, 0.9);
  text-align: center;
}

.upload-icon {
  font-size: 3rem;
  margin: 0 0 1rem 0;
  filter: grayscale(0.3) brightness(1.1);
}

/* Preview Screen */
.preview-screen {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.fullscreen-preview {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  padding-bottom: 220px;
}

.preview-with-grid {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 100%;
  max-height: 100%;
  width: 100%;
  height: 100%;
}

.preview-image {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  display: block;
}

.grid-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: auto;
  border-radius: 8px;
  cursor: grab;
}

.grid-overlay:active {
  cursor: grabbing;
}

/* Controls Island */
.controls-island {
  position: fixed;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(30px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 20px;
  padding: 1.5rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  max-width: calc(100vw - 4rem);
  width: 700px;
  z-index: 100;
}

.controls-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.file-name {
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: calc(100% - 40px);
}

.close-btn {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.8);
  width: 28px;
  height: 28px;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  padding: 0;
  flex-shrink: 0;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.3);
}

.controls-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.control-row {
  display: grid;
  grid-template-columns: 60px 1fr auto auto;
  align-items: center;
  gap: 0.75rem;
}

.control-row label {
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
  font-weight: 500;
}

.slider {
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 4px;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.15);
  outline: none;
  transition: all 0.2s ease;
}

.slider:hover {
  background: rgba(255, 255, 255, 0.2);
}

.slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: rgba(66, 185, 131, 0.9);
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid rgba(66, 185, 131, 0.5);
}

.slider::-webkit-slider-thumb:hover {
  background: rgba(66, 185, 131, 1);
  transform: scale(1.15);
  box-shadow: 0 0 12px rgba(66, 185, 131, 0.6);
}

.slider::-moz-range-thumb {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: rgba(66, 185, 131, 0.9);
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid rgba(66, 185, 131, 0.5);
}

.slider::-moz-range-thumb:hover {
  background: rgba(66, 185, 131, 1);
  transform: scale(1.15);
  box-shadow: 0 0 12px rgba(66, 185, 131, 0.6);
}

.number-input {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  padding: 0.4rem 0.6rem;
  color: white;
  font-size: 0.9rem;
  outline: none;
  transition: all 0.2s ease;
  width: 70px;
  text-align: center;
}

.number-input:focus {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(66, 185, 131, 0.5);
}

.value-display {
  color: rgba(255, 255, 255, 0.6);
  min-width: 60px;
  font-size: 0.85rem;
  font-weight: 500;
}

.control-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 0.5rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.lock-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
}

.checkbox {
  appearance: none;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.05);
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

.checkbox:checked {
  background: rgba(66, 185, 131, 0.3);
  border-color: rgba(66, 185, 131, 0.6);
}

.checkbox:checked::after {
  content: '✓';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: rgba(66, 185, 131, 1);
  font-size: 12px;
  font-weight: bold;
}

.button-group {
  display: flex;
  gap: 0.5rem;
}

.apply-btn,
.download-btn {
  padding: 0.6rem 1.5rem;
  font-size: 0.9rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;
  backdrop-filter: blur(15px);
}

.apply-btn {
  background: rgba(66, 153, 225, 0.3);
  color: white;
  border: 1px solid rgba(66, 153, 225, 0.5);
}

.apply-btn:hover {
  background: rgba(66, 153, 225, 0.4);
  border-color: rgba(66, 153, 225, 0.7);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(66, 153, 225, 0.3);
}

.download-btn {
  background: rgba(66, 185, 131, 0.3);
  color: white;
  border: 1px solid rgba(66, 185, 131, 0.5);
}

.download-btn:hover:not(:disabled) {
  background: rgba(66, 185, 131, 0.4);
  border-color: rgba(66, 185, 131, 0.7);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(66, 185, 131, 0.3);
}

.download-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.reset-offset-btn {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.8);
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  padding: 0;
  flex-shrink: 0;
}

.reset-offset-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.3);
}

/* Status Overlays */
.status-overlay,
.error-overlay {
  position: fixed;
  top: 2rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 200;
}

.status {
  padding: 1rem 2rem;
  background: rgba(66, 153, 225, 0.2);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(66, 153, 225, 0.4);
  border-radius: 12px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.95rem;
}

.error {
  padding: 1rem 2rem;
  background: rgba(239, 68, 68, 0.2);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(239, 68, 68, 0.4);
  color: rgba(255, 180, 180, 0.95);
  border-radius: 12px;
  font-size: 0.95rem;
}

/* Transitions */
.fade-enter-active, .fade-leave-active {
  transition: all 0.3s ease;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

.slide-up-enter-active {
  transition: all 0.4s ease-out;
}

.slide-up-leave-active {
  transition: all 0.3s ease-in;
}

.slide-up-enter-from {
  opacity: 0;
  transform: translateX(-50%) translateY(30px);
}

.slide-up-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(20px);
}

.bottom-text-disclaimer {
  filter: grayscale(1) brightness(0.8);
  position: fixed;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  text-align: center;
  z-index: 50;
  font-size: 0.85rem;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  h1 {
    font-size: 2rem;
  }
  
  .description {
    font-size: 1rem;
  }

  .bottom-text-disclaimer {
    font-size: 0.75rem;
    bottom: 1rem;
  }

  .upload-screen {
    align-items: flex-start;
    padding-top: 4rem;
  }
  
  .upload-area {
    padding: 2rem;
  }
  
  .upload-icon {
    font-size: 2.5rem;
  }

  .fullscreen-preview {
    padding: 1rem;
    padding-bottom: 300px;
  }

  .preview-image {
    max-height: calc(100vh - 300px);
  }

  .controls-island {
    bottom: 1rem;
    width: auto;
    max-width: calc(100vw - 4rem);
    padding: 1rem;
    font-size: 0.85rem;
  }
  
  .controls-header {
    margin-bottom: 0.75rem;
    padding-bottom: 0.75rem;
  }
  
  .file-name {
    font-size: 0.8rem;
  }
  
  .control-row {
    gap: 0.5rem;
  }
  
  .control-row label {
    font-size: 0.8rem;
  }
  
  .number-input {
    width: 60px;
    padding: 0.3rem 0.4rem;
    font-size: 0.8rem;
  }
  
  .apply-btn,
  .download-btn {
    padding: 0.5rem 1rem;
    font-size: 0.8rem;
  }
  
  .reset-offset-btn {
    width: 28px;
    height: 28px;
    font-size: 1rem;
  }
}
</style>